import { analyze, type Analysis } from './analyze';
import { t as $t } from './i18n';
import type { Project, Sym } from './types';

/*
 * The only changes the AI may propose. Each one is checked and applied here, in the
 * browser, after the engineer ticked it; nothing the model writes reaches the project
 * any other way. Drawing work (new symbols, wires, coils) is not an operation: the
 * model lists it as a manual step instead.
 */

export type AiOp =
  | { op: 'set_desc'; tag: string; desc: string }
  | { op: 'rename_tag'; tag: string; new_tag: string }
  | { op: 'delete_device'; tag: string }
  | { op: 'set_cable'; tag: string; length?: number; cable_type?: string; cross_section?: string }
  | { op: 'rename_page'; page: number; desc: string }
  | { op: 'delete_page'; page: number };

export const AI_OP_NAMES = ['set_desc', 'rename_tag', 'delete_device', 'set_cable', 'rename_page', 'delete_page'] as const;

const str = (v: unknown, n: number) => (typeof v === 'string' ? v.replace(/[\t\r\n]+/g, ' ').trim().slice(0, n) : '');
const tagOf = (v: unknown) => str(v, 40).replace(/[^\p{L}\p{N}.\-_:+=#]/gu, '');

/** Keeps the well-formed operations of the model's answer, at most 40. */
export function cleanAiOps(input: unknown): AiOp[] {
  const out: AiOp[] = [];
  for (const o of (Array.isArray(input) ? input : []).slice(0, 40) as Record<string, unknown>[]) {
    const tag = tagOf(o?.tag), page = Math.round(Number(o?.page) || 0);
    switch (o?.op) {
      case 'set_desc':
        if (tag) out.push({ op: 'set_desc', tag, desc: str(o.desc, 120) });
        break;
      case 'rename_tag': {
        const nt = tagOf(o.new_tag);
        if (tag && nt) out.push({ op: 'rename_tag', tag, new_tag: nt });
        break;
      }
      case 'delete_device':
        if (tag) out.push({ op: 'delete_device', tag });
        break;
      case 'set_cable': {
        const len = Number(o.length);
        const c: AiOp = { op: 'set_cable', tag };
        if (Number.isFinite(len) && len > 0 && len <= 10000) c.length = Math.round(len * 10) / 10;
        if (str(o.cable_type, 40)) c.cable_type = str(o.cable_type, 40);
        if (/^\d+(?:[.,]\d+)?$/.test(str(o.cross_section, 10))) c.cross_section = str(o.cross_section, 10).replace(',', '.');
        if (tag && (c.length || c.cable_type || c.cross_section)) out.push(c);
        break;
      }
      case 'rename_page':
        if (page >= 1 && str(o.desc, 120)) out.push({ op: 'rename_page', page, desc: str(o.desc, 120) });
        break;
      case 'delete_page':
        if (page >= 1) out.push({ op: 'delete_page', page });
        break;
    }
  }
  return out;
}

const norm = (t: string) => t.replace(/^-/, '').trim().toUpperCase();

/** Symbols of the device with this tag, as the reports show it (with or without structure). */
function device(pr: Project, A: Analysis, tag: string): { s: Sym; pi: number }[] {
  const byLabel = A.all.filter((it) => norm(A.devName.get(A.devKey.get(it.s.id) ?? '') ?? '') === norm(tag) || norm(A.devLabel.get(it.s.id) ?? '') === norm(tag));
  if (byLabel.length) return byLabel;
  // bare tag without structure: only when it names a single device
  const bare = A.all.filter((it) => it.s.tag && norm(it.s.tag) === norm(tag));
  return new Set(bare.map((it) => A.devKey.get(it.s.id))).size === 1 ? bare : [];
}

/** Human readable line for the proposal list. */
export function describeAiOp(o: AiOp): string {
  switch (o.op) {
    case 'set_desc': return $t('{0}: açıklama "{1}"', o.tag, o.desc);
    case 'rename_tag': return $t('{0} etiketini {1} yap', o.tag, o.new_tag);
    case 'delete_device': return $t('{0} cihazını sil (tüm sembolleri)', o.tag);
    case 'set_cable':
      return $t('{0} kablosu: {1}', o.tag, [o.cable_type, o.cross_section && o.cross_section + ' mm²', o.length && o.length + ' m'].filter(Boolean).join(' · '));
    case 'rename_page': return $t('Sayfa {0}: adı "{1}"', o.page, o.desc);
    case 'delete_page': return $t('Sayfa {0}: sil', o.page);
  }
}

/**
 * Applies the operations to the project in place. Answers what was done and what was
 * skipped (unknown tag, tag in use, last page …). Pages are deleted last, highest first,
 * so the page numbers of the other operations stay valid.
 */
export function applyAiOps(pr: Project, ops: AiOp[]): { done: string[]; skipped: string[] } {
  const done: string[] = [], skipped: string[] = [];
  const A = analyze(pr);
  const pageIds = pr.pages.map((p) => p.id);
  const skip = (o: AiOp, why: string) => skipped.push(`${describeAiOp(o)} — ${why}`);
  const dropPages = new Set<string>();
  for (const o of ops) {
    if (o.op === 'rename_page' || o.op === 'delete_page') {
      const id = pageIds[o.page - 1];
      const p = pr.pages.find((x) => x.id === id);
      if (!p) {
        skip(o, $t('sayfa yok'));
        continue;
      }
      if (o.op === 'rename_page') p.desc = o.desc;
      else dropPages.add(p.id);
      done.push(describeAiOp(o));
      continue;
    }
    const syms = device(pr, A, o.tag);
    if (!syms.length) {
      skip(o, $t('bu etikette bir cihaz bulunamadı'));
      continue;
    }
    if (o.op === 'set_desc') for (const it of syms) it.s.desc = o.desc;
    else if (o.op === 'rename_tag') {
      const nt = o.new_tag.replace(/^-/, '');
      if (A.all.some((it) => norm(it.s.tag) === norm(nt) && !syms.includes(it))) {
        skip(o, $t('{0} başka bir cihazda kullanılıyor', nt));
        continue;
      }
      const oldKeys = new Set(syms.map((it) => A.devKey.get(it.s.id)));
      for (const it of syms) it.s.tag = nt;
      // panel layouts point at devices by key: follow the new tag
      const B = analyze(pr);
      const newKey = B.devKey.get(syms[0].s.id);
      for (const p of pr.pages) for (const li of p.layout?.items ?? []) if (oldKeys.has(li.dev) && newKey) li.dev = newKey;
    } else if (o.op === 'delete_device') {
      const ids = new Set(syms.map((it) => it.s.id)), keys = new Set(syms.map((it) => A.devKey.get(it.s.id)));
      for (const p of pr.pages) {
        p.syms = p.syms.filter((s) => !ids.has(s.id));
        if (p.layout) p.layout.items = p.layout.items.filter((li) => !keys.has(li.dev));
      }
    } else if (o.op === 'set_cable') {
      const cables = syms.filter((it) => A.cables.has(it.s.id));
      if (!cables.length) {
        skip(o, $t('bu etiket bir kablo değil'));
        continue;
      }
      for (const it of cables) {
        if (o.length) it.s.props.length = String(o.length);
        if (o.cable_type) it.s.props.cableType = o.cable_type;
        if (o.cross_section) it.s.props.cs = o.cross_section;
      }
    }
    done.push(describeAiOp(o));
  }
  if (dropPages.size) {
    if (dropPages.size >= pr.pages.length) {
      skipped.push($t('Tüm sayfalar silinemez; sayfa silme işlemleri yapılmadı.'));
      done.splice(0, done.length, ...done.filter((d) => !ops.some((o) => o.op === 'delete_page' && describeAiOp(o) === d)));
    } else pr.pages = pr.pages.filter((p) => !dropPages.has(p.id));
  }
  return { done, skipped };
}
